import "./global.css";
import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import {enUS, ptBR, esES} from "@material-ui/core/locale";
import CssBaseline from "@material-ui/core/CssBaseline";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';

import Routes from "./routes";

const queryClient = new QueryClient();

const BRANDING_API = "https://auth.damineweb.work/api/branding";

const App = () => {
    const [locale, setLocale] = useState();
    const [brandingColors, setBrandingColors] = useState({ primary: "#059669", sidebar: "#0F172A" });

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    // Fetch branding colors from SSO Auth
    useEffect(() => {
        fetch(BRANDING_API)
            .then(r => r.json())
            .then(data => {
                const b = data.branding;
                if (b) {
                    setBrandingColors(prev => ({
                        primary: b.ac_primary_color || prev.primary,
                        sidebar: b.ac_sidebar_color || prev.sidebar,
                    }));
                }
            })
            .catch(() => {});
    }, []);

    const isDark = mode === "dark";

    const theme = createTheme(
        {
            typography: {
                fontFamily:
                    "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica', 'Arial', sans-serif",
                h1: { fontWeight: 600 },
                h2: { fontWeight: 600 },
                h3: { fontWeight: 600 },
                button: { textTransform: "none", fontWeight: 500 }
            },
            shape: {
                borderRadius: 10
            },
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: isDark ? "#475569" : brandingColors.primary,
                    borderRadius: 4,
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: isDark ? "#334155" : "#F3F3F3",
                    borderRadius: 4,
                },
            },
            palette: {
                type: mode,
                primary: { main: brandingColors.primary },
                textPrimary: isDark ? "#E2E8F0" : brandingColors.primary,
                borderPrimary: brandingColors.primary,
                dark: { main: isDark ? "#F1F5F9" : "#333333" },
                light: { main: isDark ? "#1E293B" : "#F3F3F3" },
                tabHeaderBackground: isDark ? "#1E293B" : "#EEE",
                optionsBackground: isDark ? "#1E293B" : "#fafafa",
                options: isDark ? "#253344" : "#fafafa",
                fontecor: isDark ? "#10B981" : "#128c7e",
                fancyBackground: isDark ? "#0F172A" : "#fafafa",
                bordabox: isDark ? "#1E293B" : "#eee",
                newmessagebox: isDark ? "#1E293B" : "#eee",
                inputdigita: isDark ? "#253344" : "#fff",
                contactdrawer: isDark ? "#1E293B" : "#fff",
                announcements: isDark ? "#1E293B" : "#ededed",
                login: isDark ? "#0F172A" : "#fff",
                announcementspopover: isDark ? "#253344" : "#fff",
                chatlist: isDark ? "#1E293B" : "#eee",
                boxlist: isDark ? "#162032" : "#ededed",
                boxchatlist: isDark ? "#0F172A" : "#ededed",
                total: isDark ? "#0F172A" : "#fff",
                messageIcons: isDark ? "#94A3B8" : "grey",
                inputBackground: isDark ? "#1E293B" : "#FFFFFF",
                barraSuperior: brandingColors.sidebar,
                boxticket: isDark ? "#1E293B" : "#EEE",
                campaigntab: isDark ? "#1E293B" : "#ededed",
                mediainput: isDark ? "#0F172A" : "#ededed",
            },
            overrides: {
                MuiButton: {
                    root: {
                        borderRadius: 999,
                        paddingLeft: 20,
                        paddingRight: 20
                    },
                    containedPrimary: {
                        boxShadow: isDark
                            ? "0 10px 25px rgba(5,150,105,0.25)"
                            : "0 10px 25px rgba(36,199,118,0.35)"
                    }
                },
                MuiPaper: {
                    rounded: {
                        borderRadius: 18
                    },
                    elevation1: {
                        boxShadow: isDark
                            ? "0 2px 12px rgba(0,0,0,0.4)"
                            : "0 10px 30px rgba(15, 23, 42, 0.08)"
                    }
                },
                MuiOutlinedInput: {
                    root: {
                        borderRadius: 10
                    }
                },
                MuiCssBaseline: {
                    '@global': {
                        body: {
                            backgroundColor: isDark ? "#0F172A" : "#fafafa",
                        },
                    },
                },
            },
            mode,
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale = i18nlocale?.substring(0, 2) ?? 'pt';

        if (browserLocale === "pt"){
            setLocale(ptBR);
        }else if( browserLocale === "en" ) {
            setLocale(enUS)
        }else if( browserLocale === "es" )
            setLocale(esES)

    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
